"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
exports.__esModule = true;
exports.MarginLine = exports.MeshNode = exports.DICOMReader = void 0;
var dicom_parser_1 = __importDefault(require("dicom-parser"));
var fast_xml_parser_1 = __importDefault(require("fast-xml-parser"));
var MarginLine = /** @class */ (function () {
    function MarginLine(name) {
        this.name = name;
        this.points = [];
    }
    MarginLine.prototype.addPoint = function (x, y, z) {
        this.points.push([x, y, z]);
    };
    return MarginLine;
}());
exports.MarginLine = MarginLine;
var MeshNode = /** @class */ (function () {
    function MeshNode(meshBufferKey, meshBufferType, meshPart, objectId, meshBuffer) {
        this.objectId = objectId;
        this.parentId = null;
        this.restoreId = null;
        this.meshBuffer = meshBuffer;
        this.meshBufferKey = meshBufferKey;
        this.meshBufferType = meshBufferType;
        this.meshPart = meshPart;
        this.texture = null;
        this.orientation = null;
        this.children = [];
        this.marginLines = [];
        this.biteMatrix = null;
        this.biteName = null;
    }
    MeshNode.prototype.addChild = function (childNode) {
        this.children.push(childNode);
    };
    MeshNode.prototype.asJson = function () {
        return {
            objectId: this.objectId,
            parentId: this.parentId,
            restoreId: this.restoreId,
            meshBuffer: this.meshBuffer,
            meshBufferKey: this.meshBufferKey,
            meshBufferType: this.meshBufferType,
            meshPart: this.meshPart,
            texture: this.texture,
            orientation: this.orientation,
            children: this.children,
            marginLines: this.marginLines,
            biteMatrix: this.biteMatrix,
            biteName: this.biteName
        };
    };
    return MeshNode;
}());
exports.MeshNode = MeshNode;
var PrivateData = /** @class */ (function () {
    function PrivateData(name, length, value) {
        this.name = name;
        this.length = length;
        this.value = value;
    }
    return PrivateData;
}());
var DICOMReader = /** @class */ (function () {
    /**
     * @param {!ArrayBuffer} buffer Buffer of the DICOM file.
     */
    function DICOMReader(buffer) {
        this.privateData = [];
        this.privateDataSet = [];
        this.meshTree = '';
        var byteArray = new Uint8Array(buffer);
        this.dataSet = dicom_parser_1["default"].parseDicom(byteArray);
        var element = this.dataSet.elements.x000911a0;
        if (element.items) {
            this.privateDataSet = element.items;
        }
    }
    /**
     * Used to parse the DICOM file then extract meshes and patient orientation.
     *
     * @return {!IData} Returns an object of the extracted meshes and patient orientation..
     */
    DICOMReader.prototype.extractData = function () {
        this.privateData = this.parsePrivateDataSet();
        this.meshTreeDescription = this.parseMeshDescription();
        return {
            meshes: this.constructMeshTree(),
            acquisitionDateTime: this.getAcqDateTime(),
            manufacturer: this.getManufacturer(),
            manufacturerModelName: this.getManufacturerModelName(),
            patientName: this.getPatientName(),
            patientId: this.getPatientId(),
            patientBirthDate: this.getPatientBirthDate(),
            patientSex: this.getPatientSex(),
            deviceSerialNumber: this.getDeviceSerialNumber(),
            softwareVersions: this.getSoftwareVersions(),
            studyInstanceUID: this.getStudyInstanceUID(),
            seriesInstanceUID: this.getSeriesInstanceUID(),
            studyID: this.getStudyID(),
            seriesNumber: this.getSeriesNumber(),
            acquisitionType: this.getPrivateString('3DACQ_FUNCTION_MODE'),
            acquisitionUserLevel: this.getPrivateString('3DACQ_USER_LEVEL'),
            patientOrientation: this.constructPatientOrientation()
        };
    };
    DICOMReader.prototype.parsePrivateDataSet = function () {
        var result = [];
        var ds;
        this.privateDataSet.forEach(function (item) {
            ds = item.dataSet;
            if (ds) {
                // name
                var name_1 = ds.string('x000911a1');
                // length
                var elem2 = ds.elements['x000911a2'];
                var length_1 = 0;
                if (elem2.length === 2) {
                    length_1 = ds.uint16('x000911a2');
                }
                else if (elem2.length === 4) {
                    length_1 = ds.uint32('x000911a2');
                }
                // value
                var elem3 = ds.elements['x000911a3'];
                var value = dicom_parser_1["default"].sharedCopy(ds.byteArray, elem3.dataOffset, elem3.length);
                var data = new PrivateData(name_1, length_1, value);
                result.push(data);
            }
        });
        return result;
    };
    DICOMReader.prototype.parseMeshDescription = function () {
        var meshTreeDesc = '';
        var item = this.privateData.find(function (item) {
            return item.name === 'MESH_TREE_DESCRIPTION';
        });
        if (item) {
            meshTreeDesc = new TextDecoder('utf-8').decode(item.value);
        }
        return meshTreeDesc;
    };
    DICOMReader.prototype.getObject3D = function (meshNode) {
        var objId = meshNode.Object3d['_id'];
        var obj = this.object3dList.Object3d.find(function (obj) {
            return obj['_id'] === objId;
        });
        return obj;
    };
    DICOMReader.prototype.isBuccalMesh = function (obj3d) {
        return obj3d.MeshPart && obj3d.MeshPart === 'BiteAverage';
    };
    DICOMReader.prototype.isNormalMesh = function (obj3d) {
        return (!(obj3d.MeshPart && obj3d.MeshPart === 'BiteBuccal') &&
            obj3d.MeshBufferKey &&
            obj3d.MeshBufferKey.startsWith('MESH_BUFFER_'));
    };
    DICOMReader.prototype.getMeshBuffer = function (bufferKey) {
        var buffer = null;
        this.privateData.every(function (item) {
            if (item.name === bufferKey) {
                buffer = item.value;
                return false;
            }
            return true;
        });
        return buffer;
    };
    DICOMReader.prototype.parseBuccalNode = function (currentNode, parentNode) {
        var obj3d = this.getObject3D(currentNode);
        var mesh = null;
        if (obj3d) {
            mesh = new MeshNode(obj3d.MeshBufferKey, obj3d.MeshBufferType, obj3d.MeshPart, obj3d._id, this.getMeshBuffer(obj3d.MeshBufferKey));
            if (parentNode && parentNode.Object3d)
                mesh.parentId = parentNode.Object3d['_id'];
            if (currentNode.MeshNode || Array.isArray(currentNode.MeshNode)) {
                if (Array.isArray(currentNode.MeshNode)) {
                    for (var _i = 0, _a = currentNode.MeshNode; _i < _a.length; _i++) {
                        var childNode = _a[_i];
                        var childMesh = this.parseNormalNode(childNode);
                        if (childMesh) {
                            childMesh.parentId = currentNode.Object3d['_id'];
                            mesh.addChild(childMesh.asJson());
                        }
                    }
                }
                else {
                    var childMesh = this.parseNormalNode(currentNode.MeshNode);
                    if (childMesh) {
                        childMesh.parentId = currentNode.Object3d['_id'];
                        mesh.addChild(childMesh.asJson());
                    }
                }
            }
        }
        return mesh;
    };
    DICOMReader.prototype.parseRestoration = function (currentNode) {
        var objId = null;
        if (currentNode && currentNode.Object3d && currentNode.Object3d['_id']) {
            objId = currentNode.Object3d['_id'];
            var obj3d = this.getObject3D(currentNode);
            if (obj3d && obj3d.MeshPart && obj3d.MeshPart === 'Restorations') {
                return objId;
            }
        }
        return null;
    };
    DICOMReader.prototype.parseNormalNode = function (currentNode, parentNode) {
        var obj3d = this.getObject3D(currentNode);
        if (obj3d) {
            var mesh = new MeshNode(obj3d.MeshBufferKey, obj3d.MeshBufferType, obj3d.MeshPart, obj3d['_id'], this.getMeshBuffer(obj3d.MeshBufferKey));
            if (parentNode && parentNode.Object3d)
                mesh.parentId = parentNode.Object3d['_id'];
            if (currentNode.MeshNode || Array.isArray(currentNode.MeshNode)) {
                if (Array.isArray(currentNode.MeshNode)) {
                    for (var _i = 0, _a = currentNode.MeshNode; _i < _a.length; _i++) {
                        var childNode = _a[_i];
                        var restoreId = this.parseRestoration(childNode);
                        if (restoreId != null) {
                            mesh.restoreId = restoreId;
                            break;
                        }
                    }
                }
                else {
                    mesh.restoreId = this.parseRestoration(currentNode.MeshNode);
                }
            }
            return mesh;
        }
        else
            return null;
    };
    DICOMReader.prototype.parseMeshNode = function (currentNode, parentNode, meshes) {
        var obj3d = this.getObject3D(currentNode);
        if (obj3d) {
            if (this.isBuccalMesh(obj3d)) {
                var mesh = this.parseBuccalNode(currentNode, parentNode);
                if (mesh) {
                    meshes.push(mesh.asJson());
                }
            }
            if (this.isNormalMesh(obj3d)) {
                var mesh = this.parseNormalNode(currentNode, parentNode);
                if (mesh) {
                    meshes.push(mesh.asJson());
                }
            }
        }
        if (currentNode.MeshNode) {
            if (Array.isArray(currentNode.MeshNode)) {
                for (var _i = 0, _a = currentNode.MeshNode; _i < _a.length; _i++) {
                    var childNode = _a[_i];
                    this.parseMeshNode(childNode, currentNode, meshes);
                }
            }
            else {
                this.parseMeshNode(currentNode.MeshNode, currentNode, meshes);
            }
        }
    };
    DICOMReader.prototype.constructMeshTree = function () {
        var options = {
            attributeNamePrefix: '_',
            ignoreAttributes: false
        };
        var meshes = [];
        this.meshDesc = fast_xml_parser_1["default"].parse(this.meshTreeDescription, options);
        if (this.meshDesc) {
            this.object3dList = this.meshDesc.trophy.MeshDescription.Object3dList;
            this.processingList = this.meshDesc.trophy.MeshDescription.ProcessingList;
            this.meshTree = this.meshDesc.trophy.MeshDescription.MeshTree;
            var meshTreeRoot = this.meshTree.MeshNode;
            this.parseMeshNode(meshTreeRoot, null, meshes);
            this.constructTextures(meshes);
            this.constructMarginLines(meshes);
            this.constructBiteMatrix(meshes);
        }
        return meshes;
    };
    DICOMReader.prototype.parseTexture = function (textureStr) {
        var options = {
            attributeNamePrefix: '_',
            textNodeName: 'value',
            ignoreAttributes: false
        };
        var textureXml = fast_xml_parser_1["default"].parse(textureStr, options);
        var preperation = textureXml.Preparation;
        var texturesList = textureXml.imageTexture.Images.Image;
        var imagecontent = null;
        if (texturesList) {
            var type = texturesList._type;
            var byte = texturesList.value;
            imagecontent = 'data:image/' + type + ';base64,' + byte;
        }
        return imagecontent;
    };
    DICOMReader.prototype.getProcessingString = function (bufferKey) {
        var buffer = null;
        if (!bufferKey)
            return buffer;
        var obj = this.privateData.find(function (item) {
            return item.name === bufferKey;
        });
        if (obj) {
            buffer = new TextDecoder('utf-8').decode(obj.value);
        }
        return buffer;
    };
    DICOMReader.prototype.getTextureKey = function (obj3dId) {
        var textureKey = null;
        if (!obj3dId)
            return textureKey;
        if (Array.isArray(this.processingList.Processing)) {
            var processing = this.processingList.Processing.find(function (processing) {
                return (processing.Object3d['_id'] === obj3dId &&
                    processing.ProcessPart === 'imageTexture');
            });
            if (processing)
                textureKey = processing.ProcessBufferKey;
        }
        else if (this.processingList.Processing) {
            var processing = this.processingList.Processing;
            if (processing.Object3d['_id'] === obj3dId &&
                processing.ProcessPart === 'imageTexture') {
                textureKey = processing.ProcessBufferKey;
            }
        }
        return textureKey;
    };
    DICOMReader.prototype.constructTextures = function (meshes) {
        var _this = this;
        meshes.forEach(function (mesh) {
            if (mesh.meshBufferType === 'PLY_TEXTURED_FILE' ||
                mesh.meshBufferType === 'PLY_FILE') {
                var textureKey = _this.getTextureKey(mesh.objectId);
                var textureStr = _this.getProcessingString(textureKey);
                if (textureStr) {
                    mesh.texture = _this.parseTexture(textureStr);
                }
            }
        });
    };
    DICOMReader.prototype.getProcessingKeys = function (obj3dId) {
        if (!obj3dId)
            return [];
        var marginLineKey = [];
        if (Array.isArray(this.processingList.Processing)) {
            for (var _i = 0, _a = this.processingList.Processing; _i < _a.length; _i++) {
                var processing = _a[_i];
                if (processing.Object3d['_id'] === obj3dId &&
                    processing.ProcessPart === 'Preparation') {
                    marginLineKey.push(processing.ProcessBufferKey);
                }
            }
        }
        else if (this.processingList.Processing) {
            var processing = this.processingList.Processing;
            if (processing.Object3d['_id'] === obj3dId &&
                processing.ProcessPart === 'Preparation') {
                marginLineKey.push(processing.ProcessBufferKey);
            }
        }
        return marginLineKey;
    };
    DICOMReader.prototype.parseMarginLine = function (marginLineStr) {
        var options = {
            attributeNamePrefix: '_',
            ignoreAttributes: false
        };
        var marginLineXml = fast_xml_parser_1["default"].parse(marginLineStr, options);
        var toothNumber = marginLineXml.Preparation.ToothNumber;
        if (toothNumber) {
            var name_2 = toothNumber._value;
            if (name_2) {
                var m = new MarginLine(name_2);
                var points = marginLineXml.Preparation.CervicalLimit.Points.Point;
                for (var _i = 0, _a = points; _i < _a.length; _i++) {
                    var point = _a[_i];
                    var w = Number(point._w) * 1.0;
                    var x = Number(point._x) / w;
                    var y = Number(point._y) / w;
                    var z = Number(point._z) / w;
                    m.addPoint(x, y, z);
                }
                return m;
            }
        }
    };
    DICOMReader.prototype.constructMarginLines = function (meshes) {
        var _this = this;
        meshes.forEach(function (mesh) {
            if (mesh.meshPart !== 'BiteAverage') {
                var marginLineKeys = _this.getProcessingKeys(mesh.restoreId);
                for (var _i = 0, marginLineKeys_1 = marginLineKeys; _i < marginLineKeys_1.length; _i++) {
                    var marginLineKey = marginLineKeys_1[_i];
                    var marginLineStr = _this.getProcessingString(marginLineKey);
                    if (marginLineStr) {
                        var marginLine = _this.parseMarginLine(marginLineStr);
                        if (marginLine) {
                            mesh.marginLines.push(marginLine);
                        }
                    }
                }
            }
        });
    };
    DICOMReader.prototype.getBiteMatrixKey = function (obj3dId) {
        if (!obj3dId)
            return null;
        var buccalNames = ['ExtraBite', 'BuccalBite'];
        var biteMatrixKey = null;
        if (Array.isArray(this.processingList.Processing)) {
            for (var _i = 0, _a = this.processingList.Processing; _i < _a.length; _i++) {
                var processing = _a[_i];
                if (processing.Object3d['_id'] === obj3dId &&
                    buccalNames.includes(processing.ProcessPart)) {
                    biteMatrixKey = processing.ProcessBufferKey;
                    break;
                }
            }
        }
        else if (this.processingList.Processing) {
            var processing = this.processingList.Processing;
            if (processing.Object3d['_id'] === obj3dId &&
                buccalNames.includes(processing.ProcessPart)) {
                biteMatrixKey = processing.ProcessBufferKey;
            }
        }
        return biteMatrixKey;
    };
    DICOMReader.prototype.parseBiteMatrix = function (biteMatrixStr) {
        var options = {
            attributeNamePrefix: '_',
            ignoreAttributes: false
        };
        var biteMatrixXml = fast_xml_parser_1["default"].parse(biteMatrixStr, options);
        var nameNode = biteMatrixXml.BuccalBite.Name;
        var name = '';
        var matrix = [];
        if (nameNode) {
            name = biteMatrixXml.BuccalBite.Name._value;
            var matrixNode_1 = biteMatrixXml.BuccalBite.Matrix;
            if (matrixNode_1) {
                matrix = Object.keys(matrixNode_1).map(function (k) { return parseFloat(matrixNode_1[k]); });
            }
        }
        return [name, matrix];
    };
    DICOMReader.prototype.constructBiteMatrix = function (meshes) {
        var _this = this;
        meshes.forEach(function (mesh) {
            if (mesh.meshPart === 'BiteAverage') {
                var biteMatrixKey = _this.getBiteMatrixKey(mesh.objectId);
                var biteMatrixStr = _this.getProcessingString(biteMatrixKey);
                if (biteMatrixStr) {
                    var _a = _this.parseBiteMatrix(biteMatrixStr), name_3 = _a[0], matrix = _a[1];
                    mesh.biteName = name_3;
                    mesh.biteMatrix = matrix;
                }
            }
        });
    };
    DICOMReader.prototype.getPrivateString = function (key) {
        var value = '';
        this.privateData.every(function (item) {
            if (item.name === key) {
                value = new TextDecoder('utf-8').decode(item.value).trim();
                return false;
            }
            return true;
        });
        return value;
    };
    DICOMReader.prototype.getAcqDateTime = function () {
        var str = this.dataSet.string('x0008002a');
        return str;
    };
    DICOMReader.prototype.getManufacturer = function () {
        var str = this.dataSet.string('x00080070');
        return str;
    };
    DICOMReader.prototype.getManufacturerModelName = function () {
        var str = this.dataSet.string('x00081090');
        return str;
    };
    DICOMReader.prototype.getPatientName = function () {
        var str = this.dataSet.string('x00100010');
        return str;
    };
    DICOMReader.prototype.getPatientId = function () {
        var str = this.dataSet.string('x00100020');
        return str;
    };
    DICOMReader.prototype.getPatientBirthDate = function () {
        var str = this.dataSet.string('x00100030');
        return str;
    };
    DICOMReader.prototype.getPatientSex = function () {
        var str = this.dataSet.string('x00100040');
        return str;
    };
    DICOMReader.prototype.getDeviceSerialNumber = function () {
        var str = this.dataSet.string('x00181000');
        return str;
    };
    DICOMReader.prototype.getSoftwareVersions = function () {
        var str = this.dataSet.string('x00181020');
        return str;
    };
    DICOMReader.prototype.getStudyInstanceUID = function () {
        var str = this.dataSet.string('x0020000d');
        return str;
    };
    DICOMReader.prototype.getSeriesInstanceUID = function () {
        var str = this.dataSet.string('x0020000e');
        return str;
    };
    DICOMReader.prototype.getStudyID = function () {
        var str = this.dataSet.string('x00200010');
        return str;
    };
    DICOMReader.prototype.getSeriesNumber = function () {
        var str = this.dataSet.string('x00200011');
        return str;
    };
    DICOMReader.prototype.constructPatientOrientation = function () {
        var str = this.dataSet.string('x00200037');
        var matrix = [];
        if (str) {
            var o = str.split('\\');
            if (o.length < 6)
                return matrix;
            var v1 = [parseFloat(o[0]), parseFloat(o[1]), parseFloat(o[2])];
            var v2 = [parseFloat(o[3]), parseFloat(o[4]), parseFloat(o[5])];
            var v3 = [0.0, 0.0, 1.0];
            matrix = [
                // Row 0
                v1[0],
                v2[0],
                v3[0],
                0.0,
                // Row 1
                v1[1],
                v2[1],
                v3[1],
                0.0,
                // Row 2
                v1[2],
                v2[2],
                v3[2],
                0.0,
                // Row 3
                0.0,
                0.0,
                0.0,
                1.0,
            ];
        }
        return matrix;
    };
    return DICOMReader;
}());
exports.DICOMReader = DICOMReader;
exports["default"] = DICOMReader;
